'use client'

import { useQuery } from '@apollo/client/react'
import { useMutation } from '@apollo/client/react'
import { useCallback, useMemo, useState } from 'react'
import { useUpdateEffect } from 'react-use'
import { createContainer } from 'unstated-next'

import { gql } from '../../__generated__/graphql/catalog/gql'
import { QueryStatus } from '../../lib/hooks/types'
import {
  FavoritesToastNotifications,
  RSCFavoritesToastNotifications,
} from '../_components/dialogs/FavoritesToastNotification.client'

import { useGraphqlClients } from './GraphqlClientsProvider.client'
import { useTrackProductFavorited } from './TrackingProvider.client'

import type {
  CustomerProfileFavoriteInput,
  FavoritesContextFavoriteFragment,
  RemoveFavoriteFromFavoritesContextMutationVariables,
} from '../../__generated__/graphql/catalog/graphql'

gql(`
    fragment FavoritesContextFavorite on CustomerProfileFavorite {
      id
      productGroupHandle
      color
      fit
      size
    }
`)

type AddFavoriteInput = Omit<CustomerProfileFavoriteInput, 'productGroupHandle' | 'size'> &
  Parameters<ReturnType<typeof useTrackProductFavorited>['trackProductFavorited']>['0']

const useFavoritesImpl = () => {
  const { personalizedClient } = useGraphqlClients()
  const [favorites, setFavorites] = useState<FavoritesContextFavoriteFragment[]>([])

  const { data, loading, error } = useQuery(
    gql(`
      query GetFavoritesContext($next: Int!) {
        customerProfile {
          id
          favorites(first: $next) {
            edges {
              node {
                ...FavoritesContextFavorite
              }
            }
          }
        }
      }
    `),
    {
      client: personalizedClient,
      variables: { next: 100 }, // Will this hardcoded 100 be an issue?
    }
  )

  const status = useMemo<QueryStatus>(() => {
    if (loading) return 'pending'
    if (error) return 'rejected'
    return 'resolved'
  }, [error, loading])

  useUpdateEffect(() => {
    if (status !== 'resolved' || !data) return
    setFavorites(
      data.customerProfile?.favorites?.edges?.map(edge => edge?.node).filter(node => !!node) ?? []
    )
  }, [data, status])

  const [addFavoriteMutation] = useMutation(
    gql(`
      mutation AddFavoriteToFavoritesContext($input: CustomerProfileFavoriteInput!) {
        createCustomerProfileFavorite(input: $input) {
          ...FavoritesContextFavorite
        }
      }
    `),
    {
      client: personalizedClient,
    }
  )

  const [removeFavoriteMutation] = useMutation(
    gql(`
      mutation RemoveFavoriteFromFavoritesContext($id: ID!) {
        deleteCustomerProfileFavorite(id: $id) {
          id
        }
      }
    `),
    {
      client: personalizedClient,
    }
  )

  const { trackProductFavorited } = useTrackProductFavorited()
  const addFavorite = useCallback(
    async ({ id, color, fit, handle, shopifyId }: AddFavoriteInput) => {
      try {
        const { error, data } = await addFavoriteMutation({
          variables: {
            input: {
              color,
              fit,
              productGroupHandle: handle,
            },
          },
          optimisticResponse: () => ({
            createCustomerProfileFavorite: {
              __typename: 'CustomerProfileFavorite',
              id: `${handle}-${color}-${fit}`,
              productGroupHandle: handle,
              fit: fit,
              color: color,
              size: null,
            },
          }),
        })

        trackProductFavorited({
          handle,
          color,
          id,
          shopifyId,
        })
        setFavorites(f => [...f, data.createCustomerProfileFavorite])
        return { error, data }
      } catch (error) {
        return { error, data: null }
      }
    },
    [addFavoriteMutation, trackProductFavorited]
  )

  const removeFavorite = useCallback(
    async (input: RemoveFavoriteFromFavoritesContextMutationVariables) => {
      await removeFavoriteMutation({
        variables: input,
      })
      setFavorites(f => f.filter(({ id }) => id !== input.id))
    },
    [removeFavoriteMutation]
  )

  return {
    favorites,
    addFavorite,
    removeFavorite,
    status,
  }
}
const FavoritesContainer = createContainer(useFavoritesImpl)
export const FavoritesProvider = ({
  children,
  rscSupported = false,
}: {
  children: React.ReactNode
  rscSupported?: boolean
}) => {
  return (
    <FavoritesContainer.Provider>
      {rscSupported ? <RSCFavoritesToastNotifications /> : <FavoritesToastNotifications />}
      {children}
    </FavoritesContainer.Provider>
  )
}

export const useFavorites = FavoritesContainer.useContainer
